import { supportingManagementPath } from "./supportingMetadata";
import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import { Project, DocumentData, SupportingDocumentMetadata } from "types";
import { useClient } from "utils/client";
import { ControlContext } from "context/Controls";
import { useConfirmation } from "context/Confirmation";
import { useSupportingDocuments } from "hooks/useSupportingDocuments";
import {
  DownloadIcon,
  TrashIcon,
  DocumentTextIcon,
  FolderOpenIcon,
  RefreshIcon,
} from "@heroicons/react/outline";
import {
  supportingDocuments,
  supportingFilename,
} from "./supportingDocumentFiles";
import {
  documentValidity,
  DocumentVisibility,
  supportingCategories,
  supportingMetadata,
  supportingVisibility,
} from "./supportingMetadata";
import { SupportingDocumentDialog } from "./SupportingDocumentDialog";
import { fetchDocumentBytes, zipDownload } from "utils/documentZip";

export const SupportingDocuments = ({
  project,
  manage = false,
  visibility,
  embedded = false,
}: {
  project: Project;
  manage?: boolean;
  visibility?: DocumentVisibility;
  embedded?: boolean;
}) => {
  const { t } = useTranslation();
  const client = useClient();
  const { displayNotification } = useContext(ControlContext);
  const { openConfirmation } = useConfirmation();
  const { remove, update } = useSupportingDocuments(project.projectdata.crn);
  const [activeDownload, setActiveDownload] = useState<string | null>(null);
  const [selected, setSelected] = useState<DocumentData>();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [validity, setValidity] = useState("");
  const [savingError, setSavingError] = useState(false);
  const busy = activeDownload !== null;
  const allDocs = supportingDocuments(project.documents).filter(
    (doc) => !visibility || supportingVisibility(doc) === visibility,
  );
  const docs = allDocs.filter((doc) => {
    const m = supportingMetadata(doc);
    return (
      (!category || m.category === category) &&
      (!validity || documentValidity(m.valid_until) === validity) &&
      [
        m.title,
        m.document_number,
        m.component,
        m.notes,
        supportingFilename(doc),
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query.toLocaleLowerCase())
    );
  });
  const failure = () =>
    displayNotification?.(t("supportingDocuments.actionFailed"), "", "error");
  const download = async (doc?: DocumentData) => {
    setActiveDownload(doc ? doc.key || doc.name : "all");
    try {
      if (doc) {
        const bytes = await fetchDocumentBytes(
          doc,
          project.projectdata.crn,
          client,
        );
        saveAs(
          new Blob([bytes], { type: "application/octet-stream" }),
          supportingFilename(doc),
        );
      } else {
        await zipDownload(
          allDocs,
          project.projectdata.crn,
          project.projectdata.product_name,
          client,
        );
      }
    } catch {
      failure();
    } finally {
      setActiveDownload(null);
    }
  };
  const save = async (metadata: SupportingDocumentMetadata) => {
    if (!selected) return;
    setSavingError(false);
    try {
      await update.mutateAsync({ doc: selected, metadata });
      setSelected(undefined);
    } catch {
      setSavingError(true);
    }
  };
  if (embedded && !allDocs.length) return null;
  return (
    <section
      className={
        embedded
          ? ""
          : "mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      }
      aria-label={t("supportingDocuments.title")}
    >
      {!embedded && (
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-gray-900">
              {t(
                manage
                  ? "supportingDocuments.uploadedFiles"
                  : "supportingDocuments.title",
              )}
            </h2>
            <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              {allDocs.length}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            {!manage && (
              <Link
                className="text-sm font-medium text-cyan-700"
                to={supportingManagementPath(project.projectdata.crn)}
              >
                {t("supportingDocuments.manage")} →
              </Link>
            )}
            {!!allDocs.length && (
              <button
                type="button"
                disabled={busy}
                onClick={() => download()}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 disabled:opacity-50"
              >
                <DownloadIcon className="h-4 w-4" />
                {t("supportingDocuments.downloadAll")}
              </button>
            )}
          </div>
        </div>
      )}
      {manage && !!allDocs.length && (
        <div className="flex flex-wrap gap-3 border-b border-gray-100 p-4">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t("documentLibrary.search")}
            placeholder={t("documentLibrary.search")}
            className="min-w-0 flex-1 rounded-md border-gray-300 text-sm"
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label={t("documentLibrary.fields.category")}
            className="max-w-full rounded-md border-gray-300 text-sm"
          >
            <option value="">{t("documentLibrary.allCategories")}</option>
            {supportingCategories.map((c) => (
              <option key={c} value={c}>
                {t(`documentLibrary.categories.${c}`)}
              </option>
            ))}
          </select>
          <select
            value={validity}
            onChange={(e) => setValidity(e.target.value)}
            aria-label={t("documentLibrary.validity")}
            className="max-w-full rounded-md border-gray-300 text-sm"
          >
            <option value="">{t("documentLibrary.allValidities")}</option>
            {["valid", "expiring", "expired", "none"].map((v) => (
              <option key={v} value={v}>
                {t(`documentLibrary.validities.${v}`)}
              </option>
            ))}
          </select>
        </div>
      )}
      {!docs.length && (
        <div className="flex flex-col items-center px-6 py-10 text-center">
          <FolderOpenIcon className="mb-3 h-8 w-8 text-gray-400" />
          <p className="text-sm font-medium text-gray-700">
            {t(
              allDocs.length
                ? "documentLibrary.noMatches"
                : "supportingDocuments.empty",
            )}
          </p>
          <p className="mt-2 max-w-md text-sm leading-6 text-gray-500">
            {t("supportingDocuments.emptyHint")}
          </p>
        </div>
      )}
      <ul className="divide-y divide-gray-100">
        {docs.map((doc) => {
          const filename = supportingFilename(doc);
          const m = supportingMetadata(doc);
          const state = documentValidity(m.valid_until);
          return (
            <li
              key={doc.key || doc.name}
              className="flex flex-col gap-3 px-4 py-4 hover:bg-gray-50 sm:flex-row sm:items-center"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="flex h-11 w-9 flex-shrink-0 items-center justify-center rounded border border-gray-200 text-gray-400">
                  <DocumentTextIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <button
                    type="button"
                    className="break-words text-left text-sm font-medium text-gray-900 hover:text-cyan-700"
                    onClick={() => {
                      setSavingError(false);
                      setSelected(doc);
                    }}
                  >
                    {m.title}
                  </button>
                  <p className="mt-1 break-words text-xs text-gray-500">
                    {[
                      t(`documentLibrary.categories.${m.category}`),
                      m.document_number,
                      m.component,
                      m.language &&
                        (m.language === "multilingual"
                          ? t("documentLibrary.multilingual")
                          : m.language.toUpperCase()),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  {manage && (
                    <p className="mt-1 text-xs text-gray-500">
                      {t(
                        `documentLibrary.visibility.${supportingVisibility(doc)}`,
                      )}
                      {m.origin &&
                        ` · ${t(`documentLibrary.origins.${m.origin}`)}`}
                    </p>
                  )}
                  {m.valid_until && (
                    <span
                      className={`mt-2 inline-block rounded px-2 py-0.5 text-xs ${state === "expired" ? "bg-red-50 text-red-700" : state === "expiring" ? "bg-yellow-50 text-yellow-800" : "bg-gray-100 text-gray-600"}`}
                    >
                      {t(`documentLibrary.validities.${state}`)} ·{" "}
                      {m.valid_until}
                    </span>
                  )}
                </div>
              </div>
              <div className="ml-12 flex flex-wrap items-center gap-2 sm:ml-0">
                <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-500">
                  {t("supportingDocuments.badge")}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setSavingError(false);
                    setSelected(doc);
                  }}
                  className="rounded px-2 py-2 text-xs font-medium text-cyan-700"
                >
                  {t("documentLibrary.details")}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => download(doc)}
                  aria-label={`${t("supportingDocuments.download")} ${filename}`}
                  className="inline-flex items-center gap-1 rounded p-2 text-xs text-cyan-700 disabled:opacity-50"
                >
                  {activeDownload === (doc.key || doc.name) ? (
                    <RefreshIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <DownloadIcon className="h-4 w-4" />
                  )}
                  <span className="sr-only">
                    {t("supportingDocuments.download")}
                  </span>
                </button>
                {manage && (
                  <button
                    type="button"
                    disabled={remove.isLoading || busy}
                    aria-label={`${t("supportingDocuments.remove")} ${filename}`}
                    className="rounded p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    onClick={() =>
                      openConfirmation(
                        () => remove.mutate(doc.name, { onError: failure }),
                        {
                          label: t("supportingDocuments.remove"),
                          subline: t("supportingDocuments.removeDescription"),
                        },
                      )
                    }
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span className="sr-only">
                      {t("supportingDocuments.remove")}
                    </span>
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {!embedded && (
        <p className="border-t border-gray-100 bg-gray-50 px-5 py-3 text-xs leading-5 text-gray-500">
          {t("supportingDocuments.currentAttachments")}
        </p>
      )}
      {selected && (
        <SupportingDocumentDialog
          key={selected.name}
          projectId={project.projectdata.crn}
          doc={selected}
          initial={supportingMetadata(selected)}
          onClose={() => setSelected(undefined)}
          onSave={save}
          busy={update?.isLoading}
          error={savingError}
          readOnly={!manage}
        />
      )}
    </section>
  );
};
